/**
 * The offline store — Arch §15, LV §127-132.
 *
 * > Offline data goes in **IndexedDB**, not localStorage.
 *
 * localStorage is synchronous, capped at a few megabytes, and stores only strings —
 * it cannot hold three photos from an inspection, which is exactly what LV §174 asks
 * for. IndexedDB can, and it is what the spec names.
 *
 * The record shape is Arch §15's, verbatim:
 *
 *     local_id · server_id · entity_type · payload · version · updated_at · sync_state
 *
 * `sync_state`: `LOCAL_ONLY · PENDING_UPLOAD · SYNCED · CONFLICT · ERROR`
 *
 * The single most important property, from Arch §15:
 *
 * > **Conflicts must never silently destroy data.** Server version 7 versus offline
 * > base version 6 yields `CONFLICT`, and the user must be able to see the differences.
 *
 * So a record that fails to sync with a `409` is kept, marked `CONFLICT`, and holds
 * *both* payloads — the local one and the server's — for the resolution screen. Nothing
 * in this module ever discards a local change.
 */

import { type DBSchema, type IDBPDatabase, openDB } from "idb";

import { offlineEnabled } from "@/app/feature-flags";

const DB_NAME = "dah-maintenance-hub";
const DB_VERSION = 2;

/** Arch §15's states, exactly. */
export type SyncState =
  "LOCAL_ONLY" | "PENDING_UPLOAD" | "SYNCED" | "CONFLICT" | "ERROR";

export type EntityType =
  | "work_order"
  | "work_order_operation"
  | "inspection"
  | "inspection_result"
  | "finding"
  | "measurement"
  | "material_issue"
  | "photo";

/** One offline record. The field list is Arch §15's. */
export interface LocalRecord {
  local_id: string;
  server_id: string | null;
  entity_type: EntityType;
  payload: Record<string, unknown>;
  /** The version the local edit was based on. The server compares against it (LV §105). */
  version: number | null;
  updated_at: string;
  sync_state: SyncState;
  /** The action to replay: an endpoint path and method. */
  operation: { method: "POST" | "PATCH"; path: string };
  /** Set on CONFLICT — the server's current state, so the user can see the difference. */
  server_payload: Record<string, unknown> | null;
  /** Set on ERROR — the German message from the LV §118 envelope. */
  error_message: string | null;
  error_code: string | null;
  /** Retries so far. A permanently failing record stops being retried, not dropped. */
  attempts: number;
}

/**
 * A photo captured offline.
 *
 * Stored as a Blob in its own store rather than base64 inside a record's payload: a
 * 3 MB photo becomes 4 MB of base64, three of them would push a single record past
 * what is comfortable, and the upload has to send bytes anyway.
 */
export interface LocalPhoto {
  local_id: string;
  /** Which local record this photo belongs to. */
  parent_local_id: string;
  /**
   * The image bytes.
   *
   * An `ArrayBuffer` rather than a `Blob` on purpose: WebKit — Safari and iPadOS — fails
   * the IndexedDB write when the stored value contains a Blob. Chromium accepts it, so a
   * Blob-based store passes every test on a desktop and loses every photo on the tablet
   * the walkdown is actually performed on (LV §128, §132). `photoBlob()` reconstructs the
   * Blob where one is needed.
   */
  bytes: ArrayBuffer;
  /** Legacy field: photos captured before the switch to `bytes`. Read, never written. */
  blob?: Blob;
  filename: string;
  mime_type: string;
  captured_at: string;
  /** The asset the photo documents — LV §174 checks photos land on the right asset. */
  asset_id: string | null;
  server_document_id: string | null;
  sync_state: SyncState;
}

/** Server data cached for reading offline (LV §127). */
export interface CachedResource {
  key: string;
  data: unknown;
  cached_at: string;
}

interface OfflineSchema extends DBSchema {
  records: {
    key: string;
    value: LocalRecord;
    indexes: {
      by_sync_state: SyncState;
      by_entity_type: EntityType;
      by_server_id: string;
    };
  };
  photos: {
    key: string;
    value: LocalPhoto;
    indexes: { by_parent: string; by_sync_state: SyncState };
  };
  cache: { key: string; value: CachedResource };
}

let dbPromise: Promise<IDBPDatabase<OfflineSchema>> | null = null;

function open(): Promise<IDBPDatabase<OfflineSchema>> {
  dbPromise ??= openDB<OfflineSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // v2 changed how a photo's bytes are stored (see `LocalPhoto.bytes`). The stores
      // themselves are unchanged, so an existing database needs no rewrite: a photo
      // captured under v1 still carries its `blob`, and `photoBlob()` reads either.
      if (oldVersion >= 1) return;

      const records = db.createObjectStore("records", { keyPath: "local_id" });
      // Indexed on sync_state because that is the sync engine's only query: "what is
      // pending". A scan over every record on every reconnect would not scale past a
      // shift's worth of work.
      records.createIndex("by_sync_state", "sync_state");
      records.createIndex("by_entity_type", "entity_type");
      records.createIndex("by_server_id", "server_id");

      const photos = db.createObjectStore("photos", { keyPath: "local_id" });
      photos.createIndex("by_parent", "parent_local_id");
      photos.createIndex("by_sync_state", "sync_state");

      db.createObjectStore("cache", { keyPath: "key" });
    },
  });
  return dbPromise;
}

function newId(): string {
  return crypto.randomUUID();
}

/**
 * Raised when something tries to queue work while offline operation is off — LV §181.
 *
 * A distinct class rather than a silent no-op, and a throw rather than a `false`
 * return: a caller that quietly dropped a technician's completed inspection because a
 * flag was off would be the silent data loss Arch §15 forbids, wearing a different
 * hat. The two call sites check `offlineEnabled()` first and show the server's own
 * error instead; this is the guard that catches a third one added later.
 */
export class OfflineDisabledError extends Error {
  constructor() {
    super(
      "Offline-Betrieb ist in dieser Installation nicht aktiviert. " +
        "Die Änderung kann nicht lokal gespeichert werden.",
    );
    this.name = "OfflineDisabledError";
  }
}

function assertOfflineEnabled(): void {
  if (!offlineEnabled()) throw new OfflineDisabledError();
}

// ---------------------------------------------------------------- records
/** Queue a change made offline. Returns the local id. */
export async function queueChange(input: {
  entity_type: EntityType;
  payload: Record<string, unknown>;
  operation: { method: "POST" | "PATCH"; path: string };
  server_id?: string | null;
  version?: number | null;
}): Promise<string> {
  assertOfflineEnabled();
  const db = await open();
  const record: LocalRecord = {
    local_id: newId(),
    server_id: input.server_id ?? null,
    entity_type: input.entity_type,
    payload: input.payload,
    version: input.version ?? null,
    updated_at: new Date().toISOString(),
    sync_state: "PENDING_UPLOAD",
    operation: input.operation,
    server_payload: null,
    error_message: null,
    error_code: null,
    attempts: 0,
  };
  await db.put("records", record);
  return record.local_id;
}

export async function getRecord(localId: string): Promise<LocalRecord | undefined> {
  return (await open()).get("records", localId);
}

export async function allRecords(): Promise<LocalRecord[]> {
  return (await open()).getAll("records");
}

export async function recordsByState(state: SyncState): Promise<LocalRecord[]> {
  return (await open()).getAllFromIndex("records", "by_sync_state", state);
}

/** The sync engine's work queue, oldest first so edits replay in the order made. */
export async function pendingRecords(): Promise<LocalRecord[]> {
  const pending = await recordsByState("PENDING_UPLOAD");
  return pending.sort((a, b) => a.updated_at.localeCompare(b.updated_at));
}

export async function countByState(): Promise<Record<SyncState, number>> {
  const records = await allRecords();
  const counts: Record<SyncState, number> = {
    LOCAL_ONLY: 0,
    PENDING_UPLOAD: 0,
    SYNCED: 0,
    CONFLICT: 0,
    ERROR: 0,
  };
  for (const record of records) counts[record.sync_state] += 1;
  return counts;
}

/** Mark a record as accepted by the server. */
export async function markSynced(
  localId: string,
  serverId: string | null,
): Promise<void> {
  const db = await open();
  const record = await db.get("records", localId);
  if (!record) return;
  await db.put("records", {
    ...record,
    server_id: serverId ?? record.server_id,
    sync_state: "SYNCED",
    error_message: null,
    error_code: null,
  });
}

/**
 * Mark a record as conflicted (Arch §15).
 *
 * The local payload is kept untouched and the server's is stored beside it, so the
 * resolution screen can show both. This is the function that makes "conflicts must
 * never silently destroy data" true: there is no code path that resolves a conflict
 * by discarding the local edit.
 */
export async function markConflict(
  localId: string,
  serverPayload: Record<string, unknown>,
  message: string,
): Promise<void> {
  const db = await open();
  const record = await db.get("records", localId);
  if (!record) return;
  await db.put("records", {
    ...record,
    sync_state: "CONFLICT",
    server_payload: serverPayload,
    error_message: message,
    error_code: "VERSION_CONFLICT",
  });
}

/** Mark a record as failed. Kept, not dropped — the user is told and can retry. */
export async function markError(
  localId: string,
  code: string,
  message: string,
): Promise<void> {
  const db = await open();
  const record = await db.get("records", localId);
  if (!record) return;
  await db.put("records", {
    ...record,
    sync_state: "ERROR",
    error_code: code,
    error_message: message,
    attempts: record.attempts + 1,
  });
}

/** Return a record to the queue — after a conflict resolution or a manual retry. */
export async function requeue(
  localId: string,
  payload?: Record<string, unknown>,
  version?: number | null,
): Promise<void> {
  const db = await open();
  const record = await db.get("records", localId);
  if (!record) return;
  await db.put("records", {
    ...record,
    payload: payload ?? record.payload,
    version: version ?? record.version,
    sync_state: "PENDING_UPLOAD",
    server_payload: null,
    error_message: null,
    error_code: null,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Remove a record.
 *
 * Only called for a record already `SYNCED` — the server has it, so the local copy is
 * redundant. A `CONFLICT` or `ERROR` record is never removed automatically; that would
 * be the silent data loss Arch §15 forbids.
 */
export async function discardSynced(localId: string): Promise<void> {
  const db = await open();
  const record = await db.get("records", localId);
  if (!record || record.sync_state !== "SYNCED") return;
  await db.delete("records", localId);
}

/** Housekeeping: drop synced records older than the retention window. */
export async function pruneSynced(olderThanDays = 7): Promise<number> {
  const cutoff = new Date(Date.now() - olderThanDays * 86_400_000).toISOString();
  const synced = await recordsByState("SYNCED");
  const db = await open();
  let removed = 0;
  for (const record of synced) {
    if (record.updated_at < cutoff) {
      await db.delete("records", record.local_id);
      removed += 1;
    }
  }
  return removed;
}

// ---------------------------------------------------------------- photos
/**
 * Store a photo captured offline.
 *
 * Compression happens before this call, in `camera.ts` — LV §132 asks for local
 * compression and a sensible resolution cap, and storing the original would fill the
 * quota during a single walkdown.
 */
export async function queuePhoto(input: {
  parent_local_id: string;
  blob: Blob;
  filename: string;
  asset_id: string | null;
}): Promise<string> {
  // Deliberately not gated on `FEATURE_OFFLINE`, unlike `queueChange`. With offline
  // switched off the caller uploads the photo straight to `/documents` instead of
  // staging it (`InspectionRunPage.addPhoto`), so nothing reaches this function.
  // Refusing here as well would make `photo_required` checklist items (LV §43-45)
  // impossible to satisfy the moment that path were ever taken, which is a functional
  // regression rather than a feature switch.
  const db = await open();
  const photo: LocalPhoto = {
    local_id: newId(),
    parent_local_id: input.parent_local_id,
    // Converted here rather than at the call site: every caller has a Blob, and only this
    // module needs to know what IndexedDB will accept.
    bytes: await input.blob.arrayBuffer(),
    filename: input.filename,
    mime_type: input.blob.type || "image/jpeg",
    captured_at: new Date().toISOString(),
    asset_id: input.asset_id,
    server_document_id: null,
    sync_state: "PENDING_UPLOAD",
  };
  await db.put("photos", photo);
  return photo.local_id;
}

/**
 * Photos waiting to upload, in the order they were taken.
 *
 * Sorted rather than left in index order, which is by `local_id` — a random UUID. A
 * walkdown's photos are a sequence a reader follows ("Riss, dann Detail, dann Umfeld"),
 * and the documents inherit their creation order from the upload, so uploading them
 * shuffled would reorder the report.
 */
/**
 * The photo as a Blob, for upload or preview.
 *
 * Accepts both shapes: `bytes` for anything captured since the switch, and the legacy
 * `blob` for a photo already sitting in a technician's database from before it.
 */
export function photoBlob(photo: LocalPhoto): Blob {
  if (photo.bytes) return new Blob([photo.bytes], { type: photo.mime_type });
  if (photo.blob) return photo.blob;
  return new Blob([], { type: photo.mime_type });
}

/** The stored size in bytes, for the offline status screen. */
export function photoSize(photo: LocalPhoto): number {
  return photo.bytes?.byteLength ?? photo.blob?.size ?? 0;
}

export async function pendingPhotos(): Promise<LocalPhoto[]> {
  const pending = await (
    await open()
  ).getAllFromIndex("photos", "by_sync_state", "PENDING_UPLOAD");
  return pending.sort((a, b) => a.captured_at.localeCompare(b.captured_at));
}

/**
 * Photos the server has, whose local copy may now be releasable.
 *
 * Whether it actually is depends on the record that references them — see
 * `releaseConfirmedPhotos` in the sync engine.
 */
export async function syncedPhotos(): Promise<LocalPhoto[]> {
  return (await open()).getAllFromIndex("photos", "by_sync_state", "SYNCED");
}

/**
 * The photos belonging to a record, by **either** of its identities.
 *
 * A photo is captured before the record that references it exists, so the screen parents
 * it to what it has at that moment — the inspection's server id. The record then gets its
 * own `local_id`. Matching on only one of the two meant the sync engine found no photos,
 * substituted no ids, and sent local ids to the server: a foreign-key violation on
 * `inspection_result.photo_document_id`, and the walkdown stuck in ERROR.
 */
export async function photosForRecord(record: {
  local_id: string;
  server_id: string | null;
}): Promise<LocalPhoto[]> {
  const db = await open();
  const found = await db.getAllFromIndex("photos", "by_parent", record.local_id);
  if (record.server_id && record.server_id !== record.local_id) {
    const seen = new Set(found.map((photo) => photo.local_id));
    for (const photo of await db.getAllFromIndex(
      "photos",
      "by_parent",
      record.server_id,
    )) {
      if (!seen.has(photo.local_id)) found.push(photo);
    }
  }
  return found.sort((a, b) => a.captured_at.localeCompare(b.captured_at));
}

/** The record a photo belongs to — matched on either identity, as above. */
export async function recordForPhoto(
  photo: LocalPhoto,
): Promise<LocalRecord | undefined> {
  const db = await open();
  const direct = await db.get("records", photo.parent_local_id);
  if (direct) return direct;
  const byServer = await db.getAllFromIndex(
    "records",
    "by_server_id",
    photo.parent_local_id,
  );
  return byServer[0];
}

export async function photosFor(parentLocalId: string): Promise<LocalPhoto[]> {
  return (await open()).getAllFromIndex("photos", "by_parent", parentLocalId);
}

export async function markPhotoSynced(
  localId: string,
  serverDocumentId: string,
): Promise<void> {
  const db = await open();
  const photo = await db.get("photos", localId);
  if (!photo) return;
  await db.put("photos", {
    ...photo,
    server_document_id: serverDocumentId,
    sync_state: "SYNCED",
  });
}

export async function markPhotoError(localId: string): Promise<void> {
  const db = await open();
  const photo = await db.get("photos", localId);
  if (!photo) return;
  await db.put("photos", { ...photo, sync_state: "ERROR" });
}

/**
 * Release a photo's local copy.
 *
 * LV §132: photos are "only released locally after the server confirms". So this
 * refuses unless the photo is `SYNCED` and carries a server document id.
 */
export async function releasePhoto(localId: string): Promise<void> {
  const db = await open();
  const photo = await db.get("photos", localId);
  if (!photo || photo.sync_state !== "SYNCED" || !photo.server_document_id) return;
  await db.delete("photos", localId);
}

// ---------------------------------------------------------------- read cache
/**
 * Cache server data for offline reading (LV §127).
 *
 * The things LV §127 requires readable offline: assigned work orders, order details,
 * the task plan, the inspection checklist.
 */
export async function cacheResource(key: string, data: unknown): Promise<void> {
  const db = await open();
  await db.put("cache", { key, data, cached_at: new Date().toISOString() });
}

export async function readCached<T>(key: string): Promise<T | null> {
  const entry = await (await open()).get("cache", key);
  return entry ? (entry.data as T) : null;
}

export async function cacheAge(key: string): Promise<string | null> {
  const entry = await (await open()).get("cache", key);
  return entry?.cached_at ?? null;
}

/**
 * Clear everything.
 *
 * Refuses while anything is unsynced: wiping a technician's shift of work because
 * they signed out would be exactly the silent data loss Arch §15 forbids. Returns
 * `false` so the caller can tell the user what is outstanding.
 */
export async function clearAll(force = false): Promise<boolean> {
  const counts = await countByState();
  const unsynced =
    counts.PENDING_UPLOAD + counts.CONFLICT + counts.ERROR + counts.LOCAL_ONLY;
  if (unsynced > 0 && !force) return false;

  const db = await open();
  await db.clear("records");
  await db.clear("photos");
  await db.clear("cache");
  return true;
}

/** Whether anything is waiting to reach the server. Drives the header indicator. */
export async function hasUnsyncedWork(): Promise<boolean> {
  const counts = await countByState();
  return counts.PENDING_UPLOAD + counts.CONFLICT + counts.ERROR + counts.LOCAL_ONLY > 0;
}

/**
 * Test seam — the fake-indexeddb tests need a fresh database per case.
 *
 * Closes the connection as well as forgetting it: `indexedDB.deleteDatabase` blocks
 * behind an open connection rather than failing, so a test that only dropped the
 * reference would hang instead of getting a clean database.
 */
export async function resetForTests(): Promise<void> {
  const pending = dbPromise;
  dbPromise = null;
  if (pending) (await pending).close();
}
